import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import _, { isEqual } from 'lodash';
import { IKImage } from 'imagekitio-react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import LinesEllipsis from 'react-lines-ellipsis';
import { AnalyticsDebug } from '../Analytics';
import { AssetContentViewer } from '../viewer';
import journeyRecommendationAsset from '../Journey';
import useDelayedInView from '../../../components/DelayInView';
import FileService from '../helper/fileHelper';
import InfiniteScroll from '../../../components/InfiniteScroll';
import Content from '../../../components/widgets/core/content';
import { GatingContext, WidgetsContext } from '../../../components/widgets/core/context';
import { getContentForFieldSelector } from '../fields';
import styles from '../assetviewer.module.scss';


export function DefaultViewerModal({ 
  scrollContainerRef, fullscreen, isPreview, show, handleClose, showRecommendSlide, nextJourneyAsset, 
  handleAssetSelection, iconUrl, name, isJourneyAsset, contentPage, fetchAdditionalContent, activeAsset, assetMappings, 
  gatingContext, state, handleJourneyPath, setNextJourneyAsset, journeyTriggered, setJourneyTrigger, journeyPaused, 
  setShowRecommendSlide, hasConditionRun, setHasConditionRun, isJourneyEnd, setIsJourneyEnd, pageSessionState, 
  setPageSessionState, journeyConfig, setJourneyConfig, handleAssetLoaded, handleAssetVisibilityChange, toggleFullScreen }) {
  return (
    <Modal
      ref={scrollContainerRef}
      backdrop
      className={styles.assetViewerModal}
      contentClassName={cx(styles.assetViewerContent, styles.headerVisible, {
        [styles.fullscreen]: fullscreen
      })}
      dialogClassName={cx(styles.assetViewerDialog, {
        [styles.fullscreen]: fullscreen,
        [styles.previewDialog]: isPreview
      })}
      fullscreen={fullscreen}
      backdropClassName={styles.backdropWrapper}
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      enforceFocus={false}
    >
      <div
        className={cx(styles.assetToast, { [styles.showToast]: showRecommendSlide })}
        style={{ cursor: 'pointer' }}
        onClick={() => nextJourneyAsset?.step?.asset && handleAssetSelection(nextJourneyAsset?.step?.asset)}
      >
        <IKImage path={iconUrl} width={79} height={75} style={{ borderRadius: "8px", objectFit: "cover" }} />
        <div className={styles.borders}></div>
        <div className={styles.details}>
          <span className={styles.tag}>Recommended Content</span>
          <LinesEllipsis
            className={styles.name}
            text={name}
            maxLine={2}
            basedOn='letters'
            ellipsis={'...'} />
          {_.get(nextJourneyAsset, 'step.asset.type', null) === 'ASSET' &&
            <ContentMetadata className={styles.additionalData} content={_.get(nextJourneyAsset, 'step.asset', '')} />}
          {_.get(nextJourneyAsset, 'step.asset.type', null) !== 'ASSET' &&
            <ContentStoryboardMetaData className={styles.additionalData} content={_.get(nextJourneyAsset, 'step.asset', '')} />}
        </div>
      </div>
      <Modal.Body className={cx(styles.assetViewerBody,
        (isJourneyAsset && (_.get(contentPage, "items", []).length < 2))
          ? styles.journeyViewerBody
          : (_.get(contentPage, "items", []).length < 2
            ? styles.singleAssetViwerBody
            : '')
      )}>
        {_.get(contentPage, "items", []).length > 1 &&
          <ContentList
            contents={contentPage.items}
            loading={contentPage.loading}
            fetchAssets={fetchAdditionalContent}
            selected={_.get(activeAsset, 'uid')}
            onSelection={handleAssetSelection} />}
        <div className={styles.assetsContainer}>
          {(assetMappings || []).filter(item => item.type === 'ASSET').map((asset, index) => {

            const active = asset.uid == _.get(activeAsset, 'uid') && !gatingContext.gating;
            const preload = state.preloadedAssets.find(item => item === asset.uid) != null;

            return (
              <AssetView
                key={asset.uid}
                asset={asset}
                preload={active || preload}
                active={active}
                fullscreen={fullscreen}
                handleJourneyPath={handleJourneyPath}
                nextJourneyAsset={nextJourneyAsset}
                setNextJourneyAsset={setNextJourneyAsset}
                journeyTriggered={journeyTriggered}
                setJourneyTrigger={setJourneyTrigger}
                journeyPaused={journeyPaused}
                activeAsset={activeAsset}
                setShowRecommendSlide={setShowRecommendSlide}
                hasConditionRun={hasConditionRun}
                setHasConditionRun={setHasConditionRun}
                isJourneyEnd={isJourneyEnd}
                setIsJourneyEnd={setIsJourneyEnd}
                isPreview={isPreview}
                pageSessionState={pageSessionState}
                setPageSessionState={setPageSessionState}
                journeyConfig={journeyConfig}
                setJourneyConfig={setJourneyConfig}
                onLoad={handleAssetLoaded}
                onVisibilityChange={handleAssetVisibilityChange}
                toggleFullScreen={toggleFullScreen}
                onClose={handleClose} />
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  )
};

const ContentCard = ({ asset, selected, onClick }) => {
  const ref = React.useRef(null);

  const name = getContentForFieldSelector(asset, 'name') || "";
  const icon = getContentForFieldSelector(asset, 'icon');
  const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

  useEffect(() => {
    if (selected) {
      scrollIntoView();
    }
  }, [selected])

  const handleClick = (asset) => {
    onClick(asset);
  }

  const scrollIntoView = () => {
    if (!ref.current) {
      return;
    }

    const scrollContainer = ref.current.parentNode;
    const parentWidth = scrollContainer.clientWidth;
    const scrollLeft = scrollContainer.scrollLeft;

    const offset = ref.current.offsetLeft;
    const cardWidth = ref.current.clientWidth;

    if (offset <= scrollLeft) {
      scrollTo(scrollContainer, offset - 25, 500);
    }

    if (offset + cardWidth > scrollLeft + parentWidth) {
      scrollTo(scrollContainer, offset + cardWidth + 25 - parentWidth, 500);
    }
  }

  const scrollTo = (element, to, duration) => {
    var start = element.scrollLeft,
      change = to - start,
      currentTime = 0,
      increment = 20;

    var animateScroll = function () {
      var easeInOutQuad = function (t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };

      currentTime += increment;
      var val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollLeft = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };

    animateScroll();
  }

  return (
    <div ref={ref} key={asset.uid} className={cx(styles.contentCard, {
      [styles.selected]: selected
    })} onClick={() => handleClick(asset)}>
      <div className={cx(styles.icon, {
        [styles.selected]: selected
      })}
      >
        <IKImage path={iconUrl} width={140} height={160} />
        <div className={styles.borders}></div>
      </div>
      <div className={styles.details}>
        <LinesEllipsis
          className={styles.name}
          text={name}
          maxLine={2}
          basedOn='letters'
          ellipsis={'...'}
        />
        {_.get(asset, 'type', null) === 'ASSET' &&
          <ContentMetadata className={styles.additionalData} content={asset} />
        }
        {_.get(asset, 'type', null) !== 'ASSET' &&
          <ContentStoryboardMetaData className={styles.additionalData} content={asset}/>
        }
        </div>
    </div>
  );
}

const ContentCardPlaceholder = () => {
  return (
    <div className={cx(styles.contentCard, styles.placeholder)}>
      <div className={cx(styles.icon)}></div>
      <div className={styles.details}>
        <div className={styles.name}>
          <span>&nbsp;</span>
          <span>&nbsp;</span>
        </div>
        <div className={cx(styles.assetMetadata)}>
          {_.times(3).map(index => {
            return (
              <div key={index} className={styles.metadata}><span>&nbsp;</span></div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ContentStoryboardMetaData = ({content, className}) =>{
  const storyboardType = _.get(content, 'content.asset.storyboardType', null);
  const getStoryBoardType =(storyboardType)=>{
    switch(storyboardType){
      case 'INTERACTIVE_CONTENT':
        return 'INTERACTIVE CONTENT'
      case 'LANDING_PAGE':
        return 'LANDING PAGE'
      case 'RESOURCE_CENTER':
        return 'RESOURCE CENTER'
      default:
        return ""
    }
}
  return (
    <div className={cx(styles.contentMetadata, className)}>
      <div className={styles.metadata}>{getStoryBoardType(storyboardType)}</div>
    </div>
  )
}

const ContentMetadata = ({ content, className }) => {
  const type = getContentForFieldSelector(content, 'contentType');
  const meta = _.get(content, 'content.asset.metadata');
  const metaData = FileService.metaData(meta);

  return (
    <div className={cx(styles.contentMetadata, className)}>
       <div className={styles.metadata}>{type}</div>
    {metaData &&
        <div className={styles.metadata}>{metaData}</div>}
    </div>
  );
}

const ContentList = ({ contents, selected, loading, fetchAssets, onSelection }) => {
  const listRef = React.useRef(null);
  const horizontalContentList = React.useRef(null);

  const [canShowLeftArrow, setCanShowLeftArrow] = useState(false);
  const [canShowRightArrow, setCanShowRightArrow] = useState(false);
  const [scrolledPosition, setScrolledPosition] = useState(0);
  const [hovered, setHovered] = useState(false);

  const handleCardClick = (content) => {
    onSelection(content);
  }

  useEffect(() => {
    calculateShowLeftArrow()
    calculateShowRightArrow()
  }, [scrolledPosition, contents])

  const handleScroll = _.debounce((element, direction) => {
    const position = element.scrollLeft;
    if (direction === "left") {
      scrollTo(element, position - 250, 150);
    }
    if (direction === "right") {
      scrollTo(element, position + 250, 150)
    }
    setScrolledPosition(position);
  }, 150);

  const scrollTo = (element, to, duration) => {
    var start = element.scrollLeft,
      change = to - start,
      currentTime = 0,
      increment = 20;
    var animateScroll = function () {
      var easeInOutQuad = function (t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };
      currentTime += increment;
      var val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollLeft = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  }

  useEffect(() => {
    var element = horizontalContentList.current;
    element.addEventListener("scroll", () => handleScroll(element));
    handleScroll(element);
    return () => {
      element.removeEventListener("scroll", () => handleScroll(element));
    };
  }, []);

  const calculateShowLeftArrow = () => {
    if (scrolledPosition != 0) {
      setCanShowLeftArrow(true)
    } else {
      setCanShowLeftArrow(false)
    }
  }

  const calculateShowRightArrow = () => {
    var element = horizontalContentList.current;
    var scrollWidth = element?.scrollWidth;
    var clientWidth = element?.clientWidth;
    var offsetWidth = element?.offsetWidth;

    if (scrolledPosition > scrollWidth) {
      setCanShowRightArrow(false);
    }
    if (scrolledPosition === (scrollWidth - clientWidth)) {
      setCanShowRightArrow(false)
    }
    if (scrolledPosition !== (scrollWidth - clientWidth)) {
      setCanShowRightArrow(true)
    }
    if ((scrollWidth - Math.round(scrolledPosition)) === offsetWidth) {
      setCanShowRightArrow(false)
    }
  }

  const handleClick = (direction) => {
    var element = horizontalContentList.current;
    handleScroll(element, direction);
  }

  return (
    <div ref={listRef} className={cx(styles.contentList)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <span onClick={() => handleClick("left")} className={cx(styles.arrows, styles.leftArrow,{ [styles.showLeftArrow]: (canShowLeftArrow && hovered)})}>
        <i className={cx('cleverstory-drop-down-left')} />
      </span>
      <div className={cx(styles.cardsContainer)} ref={horizontalContentList}>
        <InfiniteScroll direction='horizontal' loading={loading} loadData={fetchAssets}>
          {
            contents && contents.map((asset, index) => {
              const assetSelected = _.get(asset, 'uid') === selected;
              return (
                <ContentCard
                  key={asset.uid}
                  asset={asset}
                  selected={assetSelected}
                  onClick={(content) => handleCardClick(content)}
                />
              )
            })
          }

          {loading && _.times(3).map(index => {
            return <ContentCardPlaceholder key={index} />
          })}
        </InfiniteScroll>
      </div>
      <span onClick={() => handleClick("right")} className={cx(styles.arrows, styles.rightArrow,{ [styles.showRightArrow]: (canShowRightArrow && hovered)})}>
        <i className={cx('cleverstory-drop-down-right')} />
      </span>
    </div>
  )
}

const AssetHeader = ({ asset, analytics, fullscreen, toggleFullScreen, canDownload, canShare, onDownload, onClose, debug }) => {
  const name = getContentForFieldSelector(asset, 'name');
  const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
  const content_type = _.get(asset, 'content.asset.metadata.content_type');

  const [showDropdown, setShowDropdown] = useState(false);

  const removeQueryParamsFromURL = (url) => {
    const newUrl = new URL(url);
    return `${newUrl.origin}${newUrl.pathname}`;
  }

  const assetAlias = `${_.get(asset, 'content.domain','')}${_.get(asset, 'url', '')}`;
  const isLayout = _.includes(_.get(asset, 'uid'), 'layout');
  const assetUrl = isLayout ?  assetAlias : removeQueryParamsFromURL(window.location.href);

  const copyAssetUrl = (url) => {
    const input = document.createElement('input');
    input.value = url;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();

    document.execCommand('copy');
    document.body.removeChild(input);
  };

  const handleSocialShare = (type) => {
      let utmLink = assetUrl + "?" + new Date().getTime();
      utmLink = encodeURI(utmLink);

      let winTop = (screen.height - 500) / 2;
      let winLeft = (screen.width - 575) / 1.75;
      let url = '';

      switch (type) {
        case 'facebook':
          url = `https://www.facebook.com/sharer.php?u=${utmLink}&title=${name}`;
          break;
        case 'linkedIn':
          url = `https://www.linkedin.com/shareArticle?mini=true&url=${utmLink}&title=${name}`;
          break;
        case 'twitter':
          url = `https://twitter.com/share?url=${utmLink}&title=${name}`;
          break;
      }
      window.open(url, 'sharer', 'top=' + winTop + ',left=' + winLeft + ',toolbar=0,status=0,width=' + 500 + ',height=' + 575);
  }

  return (
    <div className={styles.header}>
      <div className={styles.details}>
        <div className={styles.name}>{name}</div>
        <ContentMetadata content={asset} />
      </div>
      <div className={styles.actions}>
        <div className={styles.column1}>
          {debug &&
            <Button className={cx(styles.action, styles.debug)}>
              <AnalyticsDebug asset={asset.content.asset} analytics={analytics} />
            </Button>
          }
          <React.Fragment>
            {canShare && <Dropdown 
              className={cx(styles.action, styles.shareDropdown)} 
              show={showDropdown} 
              onToggle={() => setShowDropdown(!showDropdown)} 
              onMouseLeave={() => setShowDropdown(false)}
            >
              <Dropdown.Toggle id="dropdown-custom-components">
                <i className={cx('cleverstory-share')} />
                <span>Share</span>
                <i className={cx(styles.arrow, 'cleverstory-drop-down')} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={()=>copyAssetUrl(assetUrl)}>
                  <i className={cx('cleverstory-link')}/>Copy Link
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('facebook')}>
                  <i className={cx('cleverstory-facebook')} />Facebook
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('linkedIn')}>
                  <i className={cx('cleverstory-linkedin')}/>LinkedIn
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleSocialShare('twitter')}>
                  <i className={cx('cleverstory-twitter')} />Twitter
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>}
            {downloadUrl && canDownload && content_type!="application/url" &&
              <Button className={cx(styles.action, styles.download)} onClick={() => { onDownload(asset) }}>
                <i className={cx('cleverstory-download')} />
                <span>Download</span>
              </Button>
            }
          </React.Fragment>
        </div>

        <div className={styles.column2}>
          <Button className={cx(styles.action, styles.fullscreen)} onClick={toggleFullScreen}>
            <i className={`cleverstory-${fullscreen ? 'minimize-editor' : 'expand-editor'}`} />
          </Button>
          <Button className={cx(styles.action, styles.close)} onClick={onClose}>
            <i className={cx('cleverstory-close')} />
          </Button>
        </div>
      </div>
    </div>
  )
}

const AssetView = ({
  asset, preload, active, fullscreen,
  onLoad, onVisibilityChange, toggleFullScreen, onClose,
  handleJourneyPath, nextJourneyAsset, setNextJourneyAsset,
  journeyTriggered, setJourneyTrigger, journeyPaused, activeAsset, setShowRecommendSlide,
  hasConditionRun, setHasConditionRun, isJourneyEnd, setIsJourneyEnd, isPreview,
  pageSessionState, setPageSessionState, journeyConfig, setJourneyConfig
}) => {

  const contentContext = React.useContext(Content.Context);
  const gatingContext = React.useContext(GatingContext);
  const widgetContext = React.useContext(WidgetsContext);

  const [initialised, setInitialised] = React.useState(preload);
  const [analytics, setAnalytics] = React.useState(null);
  const [nextAsset, setNextAsset] = React.useState(null);
  const [gatingConfigurations, setGatingConfigurations] = useState(_.get(asset, 'content.gatingConfigurations', []));
  const [showDownload, setShowDownload] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [checkPageSession, setCheckPageSession] = useState(false);
  const journeyConfigurations = useSelector(state => _.get(state, `content.journey.${asset?.uid}.item`));
  const isJourneyPaused = useSelector(state => _.get(state, `content.journey.${activeAsset?.uid}.item`));
  const journeyFailed = useSelector(state => _.get(state, `content.journey.${activeAsset?.uid}.error`));
  const activeSession = useSelector(state => _.get(state, `content.analytics.${asset?.uid}.item.id`));
  const disableDownload = _.get(asset, 'content.disableDownload', false);
  const disableShare = _.get(asset, 'content.disableShare', false);
  const frameAssetViewer = document?.getElementById("iframeembed");

  const [activeAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerDialog}`),
    onChange: (visible) => {
      onVisibilityChange(asset, visible);
    }
  });

  useEffect(() => {
      const updatedItems = gatingConfigurations.filter(({id}) => !gatingContext.assetConfigurations.includes(id));
      setGatingConfigurations(updatedItems);
  }, [gatingContext.assetConfigurations])

  useEffect(() => {
    if(!isJourneyEnd && !journeyConfigurations && !journeyTriggered && !isPreview) {
      if (checkPageSession && pageSessionState ) {
        contentContext.fetchAssetJourney(asset, null, null, pageSessionState);
        setCheckPageSession(false);
      } else if (checkPageSession && frameAssetViewer && !journeyTriggered && !isPreview) {
        const pageSession = frameAssetViewer?.getAttribute('data-page-session');
        setPageSessionState(pageSession);
        contentContext.fetchAssetJourney(asset, null, null, pageSession);
        setCheckPageSession(false);
      }
    } else {
      setCheckPageSession(false);
    }
  },[pageSessionState, frameAssetViewer]);

  useEffect(() => {
    setShowDownload(!disableDownload && gatingConfigurations.length === 0);
    setShowShare(!disableShare && gatingConfigurations.length === 0)
  }, [])

  useEffect(() => {
    if (!disableDownload && gatingContext.disabled) {
      setShowDownload(true);
    }
  }, [gatingContext.disabled])

  useEffect(() => {
    if (!disableShare && gatingContext.disabled) {
      setShowShare(true);
    }
  }, [gatingContext.disabled])

  const [preloadAssetRef] = useDelayedInView({
    rootMargin: '-50% 0% -50% 0%',
    root: document.querySelector(`.${styles.assetViewerContent}`),
    skip: initialised,
    onChange: (visible) => {
      if (visible) {
        setInitialised(true);
      }
    }
  });

  const setRefs = React.useCallback(
    (node) => {
      activeAssetRef(node)
      preloadAssetRef(node)
    },
    [activeAssetRef, preloadAssetRef]
  );

  useEffect(() => {
    if (preload) {
      setInitialised(true);
    }
  }, [preload]);

  useEffect(() => {
    if (activeAsset && isJourneyPaused && !isJourneyEnd) {
      const isPausedJourneyActive =
        activeAsset.uid === journeyPaused?.assetId &&
        isJourneyPaused?.journeyExecution?.paused;

      if (isPausedJourneyActive && !hasConditionRun) {
        const recommendedAsset = journeyRecommendationAsset(
          analytics,
          isJourneyPaused,
          nextJourneyAsset,
          setNextJourneyAsset,
          hasConditionRun,
          setHasConditionRun,
          setJourneyTrigger,
          setShowRecommendSlide,
          setIsJourneyEnd
        );
        handleNextAsset(recommendedAsset);
      }
    }
  }, [activeAsset, isJourneyPaused]);

  const handleNextAsset =(assetData)=>{
    if (!isEqual(assetData?.asset, nextAsset)) {
      setNextAsset(assetData?.asset)
    }
  }

  useEffect(() => {
    if (analytics && !journeyPaused?.paused && !hasConditionRun && !isJourneyEnd && journeyConfigurations) {

      const assetConditon = activeAsset?.uid == journeyConfig?.asset;
      const journeyCondition = journeyConfigurations?.id == journeyConfig?.journey;
      const nullCondition = (journeyConfig?.journey == null && journeyConfig?.asset == null);

      if ( nullCondition || (!assetConditon && !journeyCondition) ) {
        setJourneyConfig({
          asset: activeAsset?.uid,
          journey: journeyConfigurations?.id
        })
      }

      if (nullCondition || (assetConditon && journeyCondition)) {
        const recommendedAsset = journeyRecommendationAsset(
          analytics,
          journeyConfigurations,
          nextJourneyAsset,
          setNextJourneyAsset,
          hasConditionRun,
          setHasConditionRun,
          setJourneyTrigger,
          setShowRecommendSlide,
          setIsJourneyEnd
        );

        handleNextAsset(recommendedAsset);
      }
    }
  }, [analytics, journeyConfigurations, journeyPaused]);

  useEffect(() => {
    if (nextAsset) {
      handleJourneyPath(nextAsset);
    }
  }, [nextAsset]);

  useEffect(() => {
    if (journeyFailed) {
      setJourneyTrigger(true);
    }
  }, [journeyFailed]);

  const handleViewerLoaded = () => {
    if (!onLoad) return;

    onLoad(asset.uid);
  };

  const handleDownload = (asset) => {
    if(disableDownload) {
      return;
    }

    const downloadUrl = _.get(asset, 'content.asset.downloadUrl');
    window.open(downloadUrl, "_blank");

    contentContext.onDownload(asset);
  }

  useEffect(() => {
    if(gatingContext.configuration) {
      return;
    }
    if(analytics) {
      triggerAssetGating(analytics)
    }
  }, [analytics])

  useEffect(() => {
    if(analytics && analytics?.duration == 0 && analytics?.percentageRead == 0) {

      const frameAssetViewer = document?.getElementById("iframeembed");

      if (!isJourneyEnd && !journeyConfigurations && !isPreview) {
        if (nextJourneyAsset?.step != null) {
          const { id: stepId } = nextJourneyAsset?.step;
          const { id: journeyExecutionId } = nextJourneyAsset?.journeyExecution;
          contentContext.fetchAssetJourney(asset, stepId, journeyExecutionId, pageSessionState);
        } else if (!journeyTriggered && !isPreview) {
          if (pageSessionState != null) {
            contentContext.fetchAssetJourney(asset, null, null, pageSessionState);
          } else if (frameAssetViewer) {
            const pageSession = frameAssetViewer?.getAttribute('data-page-session');
            setPageSessionState(pageSession);
            contentContext.fetchAssetJourney(asset, null, null, pageSession);
          } else {
            setCheckPageSession(true);
          }
        }
      }

      if (onLoad) {
        onLoad(asset.uid, journeyConfigurations);
      }

    }
  }, [analytics])

  const triggerAssetGating = ({ duration, percentageRead }) => {
    if (gatingContext.preview) {
      return;
    }

    const assetType = ["application/url", "image/gif", "image/tiff", "image/png", "image/bmp", "image/jpeg", "image/svg+xml"];
    const isAssetContain = _.includes(assetType, _.get(asset, 'content.asset.metadata.content_type'))
    const effectivePercentage = (isAssetContain && duration > 10) ? 100 : percentageRead;

    gatingConfigurations.every(item => {
      if(!item.enabled) {
        return true;
      }

      var triggerValue = _.get(item, 'trigger.value', 0);
      var limit = duration;

      if (_.get(item, 'trigger.type', '') === "PERCENTAGE") {
        limit = effectivePercentage;
      }

      if (triggerValue <= limit) {
        var gating = _.get(item, 'gating.id', null);

        if (gating) {
          widgetContext.fetchGating(gating);
          gatingContext.triggerGating(_.get(item, 'trigger.type', ''), null, {
            ...item,
            target: {
              ...item.target,
              assetModal: true
            }
          });
          return false;
        }
      }

      return true;
    })
  }

  return (
    <div ref={setRefs} className={styles.asset} data-asset={asset.uid}>
      <AssetHeader
        asset={asset}
        analytics={analytics}
        fullscreen={fullscreen}
        toggleFullScreen={toggleFullScreen}
        canDownload={showDownload}
        canShare={showShare}
        onDownload={handleDownload}
        onClose={onClose}
        debug={contentContext.debug}
      />
      <div className={cx(styles.body, {
        [styles.initialised]: initialised
      })}>
        {initialised &&
          <AssetContentViewer
            asset={asset}
            active={active}
            onLoad={handleViewerLoaded}
            onAnalyticsChange={(analytics) => {
              if (activeSession || active) {
                setAnalytics(analytics);
                contentContext.onAnalyticsChange(asset, analytics);
              }
            }}
            onDebugAnalytics={(analytics) => {
              if (contentContext.debug) {
                setAnalytics(analytics);
              }
            }}
          />
        }
      </div>
    </div>
  )
}



